import { useState } from 'react'
import { useParams } from "react-router-dom";
import { useGetAlarmClassByIdQuery, Behavior, AlarmClass, useCreateAlarmClassMutation, CreateAlarmClassCommand, useUpdateAlarmClassMutation, UpdateAlarmClassCommand } from '../../services/alarmClasses'
import { CardWithHeader } from '../../components';
import { LoadingView } from '../../components'
import { Page } from '../../components';
import { useStyletron } from 'baseui'
import { Block } from 'baseui/block';
import { Button, KIND, SIZE, SHAPE } from "baseui/button";
import { Input } from "baseui/input";
import {
    LabelLarge,
    LabelSmall,
    DisplayXSmall,
    ParagraphSmall,
} from 'baseui/typography';
import { Checkbox, STYLE_TYPE } from 'baseui/checkbox';
import { ButtonGroup } from 'baseui/button-group';
import { FormControl } from 'baseui/form-control';

type BehaviorEditViewProps = {
    behavior: Behavior,
    name: string,
    showAck: boolean,
    setBehavior: React.Dispatch<React.SetStateAction<Behavior>>
}
const BehaviorEditView = ({ behavior, name, setBehavior, showAck }: BehaviorEditViewProps) => {

    const [css, theme] = useStyletron();
    const [details, setDetails] = useState(false);

    const colors = Object.entries(theme.colors).filter(x =>
        x[0].includes('positive') ||
        x[0].includes('negative') ||
        x[0].includes('warning') ||
        x[0].includes('accent')
    ).map((c: any, index: number) => {
        return {
            id: index,
            label: c[0],
            color: c[1]
        }
    });



    const selFg = colors.findIndex((elm: any) => elm.color === behavior.foregroundColor);
    const selBg = colors.findIndex((elm: any) => elm.color === behavior.backgroundColor);
    const [selectedForeground, setSelectedForeground] = useState(selFg);
    const [selectedBackground, setSelectedBackground] = useState(selBg);


    const onSetAckNecessary = (value: boolean) => {
        setBehavior((prevState: Behavior) => ({
            ...prevState,
            ackNecessary: value
        }));
    }

    const onSetForegroundColor = (index: number, value: string) => {
        setBehavior((prevState: Behavior) => ({
            ...prevState,
            foregroundColor: value
        }));
        setSelectedForeground(index);
    }
    const onSetBackgroundColor = (index: number, value: string) => {
        setBehavior((prevState: Behavior) => ({
            ...prevState,
            backgroundColor: value
        }));
        setSelectedBackground(index);
    }


    return (
        <CardWithHeader header={name} subHeader='defines the alarm behavior'>
            {showAck ? <Checkbox onChange={(e: any) => onSetAckNecessary(e.target.checked)} checkmarkType={STYLE_TYPE.toggle_round} checked={behavior.ackNecessary} >Acknowledge necessary</Checkbox> : <></>}

            {!details && <Button kind='tertiary' size='mini' onClick={() => setDetails(old => true)}>more color options ...</Button>}
            {details && <Button kind='tertiary' size='mini' onClick={() => setDetails(old => false)}>less options ...</Button>}
            {details && (<>

                <LabelSmall>Foreground color</LabelSmall>

                <ButtonGroup overrides={{
                    Root: {
                        style: {
                            flexWrap: 'wrap'
                        }
                    }
                }} size={SIZE.mini} mode='radio' selected={selectedForeground}>
                    {colors?.map((item: any, index: number) =>
                    (<Button key={index} onClick={(e: any) => onSetForegroundColor(index, item.color)}>
                        <span className={css({
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: item.color
                        })}></span>
                    </Button>))
                    }
                </ButtonGroup>


                <LabelSmall>Background color</LabelSmall>
                <ButtonGroup overrides={{
                    Root: {
                        style: {
                            flexWrap: 'wrap'
                        }
                    }
                }} size={SIZE.mini} mode='radio' selected={selectedBackground}>

                    {colors?.map((item: any, index: number) =>
                    (<Button key={index} onClick={(e: any) => onSetBackgroundColor(index, item.color)}>
                        <span className={css({
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: item.color
                        })}></span>
                    </Button>))
                    }

                </ButtonGroup>
            </>)}

        </CardWithHeader>)
}

interface EditViewProps {
    name: string,
    onNameChanged: any;
    priority: number,
    onPriorityChanged: any;
    iconUrl: string,
    onIconUrlChanged: any;
    alarmClass: AlarmClass;

    onBehavior: Behavior;
    offBehavior: Behavior;
    ackedBehavior: Behavior;
    setOnBehavior: React.Dispatch<React.SetStateAction<Behavior>>;
    setAckedBehavior: React.Dispatch<React.SetStateAction<Behavior>>;
    setOffBehavior: React.Dispatch<React.SetStateAction<Behavior>>;
}

const EditView = (props: EditViewProps) => {
    const [css, theme] = useStyletron();
    const colorSchemes = [
        {
            name: 'Errors',
            descr: 'Faults and errors when something is not working any more',
            onBackgroundColor: theme.colors.notificationWarningBackground,
            onForegroundColor: theme.colors.warning500,
            ackBackgroundColor: 'transparent',
            ackForegroundColor: theme.colors.warning600,
            offBackgroundColor: 'transparent',
            offForegroundColor: theme.colors.warning200,
        },
        {
            name: 'Alarms',
            descr: 'Process related alarms like limit high, freeze protection, fire etc.',
            onBackgroundColor: theme.colors.notificationNegativeBackground,
            onForegroundColor: theme.colors.negative500,
            ackBackgroundColor: 'transparent',
            ackForegroundColor: theme.colors.negative600,
            offBackgroundColor: 'transparent',
            offForegroundColor: theme.colors.negative200,
        },
        {
            name: 'Info',
            descr: 'Informative messages like Manual Mode etc.',
            onBackgroundColor: theme.colors.notificationInfoBackground,
            onForegroundColor: theme.colors.accent500,
            ackBackgroundColor: 'transparent',
            ackForegroundColor: theme.colors.accent600,
            offBackgroundColor: 'transparent',
            offForegroundColor: theme.colors.accent200,
        },
        {
            name: 'Events',
            descr: 'Events',
            onBackgroundColor: theme.colors.notificationPositiveBackground,
            onForegroundColor: theme.colors.positive500,
            ackBackgroundColor: 'transparent',
            ackForegroundColor: theme.colors.positive600,
            offBackgroundColor: 'transparent',
            offForegroundColor: theme.colors.positive200,
        },
        {
            name: 'Low Priority',
            descr: 'Low priority alarms',
            onBackgroundColor: theme.colors.notificationInfoBackground,
            onForegroundColor: theme.colors.notificationInfoText,
            ackBackgroundColor: 'transparent',
            ackForegroundColor: theme.colors.primary600,
            offBackgroundColor: 'transparent',
            offForegroundColor: theme.colors.primary400,
        }
    ]

    function applyTemplate(template: any) {
        const newOn = { ...props.onBehavior };
        newOn.foregroundColor = template.onForegroundColor;
        newOn.backgroundColor = template.onBackgroundColor;
        props.setOnBehavior(newOn);

        const newAcked = { ...props.ackedBehavior };
        newAcked.foregroundColor = template.ackForegroundColor;
        newAcked.backgroundColor = template.ackBackgroundColor;
        props.setAckedBehavior(newAcked);

        const newOff = { ...props.offBehavior };
        newOff.foregroundColor = template.offForegroundColor;
        newOff.backgroundColor = template.offBackgroundColor;
        props.setOffBehavior(newOff);
    }

    return (<>
        <CardWithHeader header="Basic Settings">
            <FormControl
                label={() => "Name"}
                caption={() => "enter a descriptive name for the alarm group"}
            >
                <Input
                    name="name"
                    id="name"
                    placeholder="Please enter a name here"
                    onChange={props.onNameChanged} value={props.name}></Input>
            </FormControl>
            <FormControl
                label={() => "Priority"}
                caption={() => "enter a priority value - a higher value is more important"}
            >
                <Input
                    type='number'
                    id="priority"
                    placeholder="Please enter a priority value here"
                    onChange={props.onPriorityChanged} value={props.priority?.toString()}
                />
            </FormControl>
           {/* <FormControl
                label={() => "Icon"}
                caption={() => "select an icon for the alarm group"}
            >
                <>
                    <Input
                    startEnhancer={
                        <img alt="no image defined"
                        height={16}
                        src={props.iconUrl}></img>
                    }
                        name="icon"
                        placeholder="Please enter an icon url"
                        onChange={props.onIconUrlChanged} value={props.iconUrl}
                    />
                </>
                </FormControl>*/}
        </CardWithHeader>
          <CardWithHeader header="Templates" subHeader='use these templates to get well choosen color settings'>
            <Block justifyContent='flex-start'>
                {colorSchemes?.map((item: any, index: number) => (
                    <Button kind='tertiary' onClick={() => applyTemplate(item)} >
                        <Block maxWidth='200px' gridGap='20px' display='flex' flexDirection='column' >
                            <span className={css({
                                borderRadius: '10px',
                                padding: '20px',
                                backgroundColor: item.onBackgroundColor,
                                color: item.onForegroundColor
                            })}>{item.name}</span>
                            <ParagraphSmall >{item.descr}</ParagraphSmall>
                        </Block>
                    </Button>
                ))}
            </Block>
        </CardWithHeader>
        <BehaviorEditView showAck name="On Behavior" behavior={props.onBehavior} setBehavior={props.setOnBehavior}></BehaviorEditView>
        <BehaviorEditView showAck={false} name="Acked Behavior" behavior={props.ackedBehavior} setBehavior={props.setAckedBehavior}></BehaviorEditView>
        <BehaviorEditView showAck name="Off Behavior" behavior={props.offBehavior} setBehavior={props.setOffBehavior}></BehaviorEditView>
                      
    </>)
}

function createEmpty(theme: any): AlarmClass {
    return {
        id: '',
        name: '',
        priority: 3,
        ackedBehavior: {
            backgroundColor: theme.colors.backgroundLightAccent,
            foregroundColor: theme.colors.negative500,
        },
        onBehavior: {
            backgroundColor: '',
            foregroundColor: theme.colors.negative,
            ackNecessary: true,
        },
        offBehavior: {
            backgroundColor: '',
            foregroundColor: theme.colors.negative,
            ackNecessary: false,
        },
        iconUrl: 'https://th.bing.com/th/id/R.cd9a1da7dc41ef2997cf05c1214e8157?rik=8GK15hL1OgnhCw&pid=ImgRaw&r=0'
    }
}

const EditMainView = (props: any) => {


    const alarmClass: AlarmClass = props.alarmClass;

    const [createNewClass, createNewClassResult] = useCreateAlarmClassMutation()
    const [updateClass, updateClassResult] = useUpdateAlarmClassMutation()

    const [name, setName] = useState(alarmClass.name);
    const [priority, setPriority] = useState(alarmClass.priority);
    const [iconUrl, setIconUrl] = useState(alarmClass.iconUrl);

    const onNameChanged = (e: any) => setName(e.target.value)
    const onPriorityChanged = (e: any) => setPriority(e.target.value)
    const onIconUrlChanged = (e: any) => setIconUrl(e.target.value)

    const [onBehavior, setOnBehavior] = useState(alarmClass.onBehavior);
    const [offBehavior, setOffBehavior] = useState(alarmClass.offBehavior);
    const [ackedBehavior, setAckedBehavior] = useState(alarmClass.ackedBehavior);

    const isNew = alarmClass.id === '';

    function onSaveClick(): void {
        if (isNew) {
            const cmd: CreateAlarmClassCommand = {
                name: name,
                priority: priority,
                iconUrl: iconUrl,
                onBehavior: onBehavior,
                offBehavior: offBehavior,
                ackedBehavior: ackedBehavior,
            }
            createNewClass(cmd);
        }
        else {
            const cmd: UpdateAlarmClassCommand = {
                id: alarmClass.id,
                name: name,
                priority: priority,
                iconUrl: iconUrl,
                onBehavior: onBehavior,
                offBehavior: offBehavior,
                ackedBehavior: ackedBehavior,
            }
            updateClass(cmd);
        }
    }

    const header = isNew ? "New Alarm Class" : "Edit Alarm Class";
    const saveBtn = (<Button onClick={onSaveClick} isLoading={createNewClassResult.isLoading || updateClassResult.isLoading}>Save</Button>);
    return (
        <Page header={header} subheader={name} actionButton={saveBtn} showNavigateBack>
            <EditView
                name={name} onNameChanged={onNameChanged}
                priority={priority} onPriorityChanged={onPriorityChanged}
                iconUrl={iconUrl} onIconUrlChanged={onIconUrlChanged}
                onBehavior={onBehavior} setOnBehavior={setOnBehavior}
                offBehavior={offBehavior} setOffBehavior={setOffBehavior}
                ackedBehavior={ackedBehavior} setAckedBehavior={setAckedBehavior}
                alarmClass={alarmClass} />
        </Page>
    )
}

export const EditAlarmClass = () => {
    const prms = useParams();
    const alarmClassId = prms.alarmClassId;
    const { data, error, isLoading, isSuccess } = useGetAlarmClassByIdQuery(alarmClassId ? alarmClassId : '')

    return (
        <LoadingView error={error} isLoading={isLoading} isSucess={isSuccess}>
            <EditMainView alarmClass={data}></EditMainView>
        </LoadingView>)
}

export const NewAlarmClass = () => {
    const [css, theme] = useStyletron();
    return (<EditMainView alarmClass={createEmpty(theme)}></EditMainView>)
}
